import React, {useState} from 'react'
import {Box, Link, HStack, Image, VStack, Text} from '@chakra-ui/react3'
import {FormattedMessage, useIntl} from 'react-intl'
import PropTypes from 'prop-types'
import useProductSpecialOffers from '../../../../hooks/use-product-special-offers'
import {ChevronDownIcon, ChevronUpIcon} from '../../../../leap/components/icons'
import Slider from '../../../../leap/components/cms/slider'
import useSpecialOffer from '../../../../hooks/use-special-offer'
import StyledContent from '../../../../components/styled-content'

const PromotionItem = ({promotion, isActive}) => {
    const [isOpen, setIsOpen] = useState(false)
    const [isIconURLValid, setIsIconURLValid] = useState(true)

    const {fontColor} = useSpecialOffer(promotion)

    const [prevIsActive, setPrevIsActive] = useState(isActive)

    if (isActive !== prevIsActive) {
        setPrevIsActive(isActive)

        if (!isActive) {
            setIsOpen(false)
        }
    }

    return (
        <VStack align="start" width="full" gap="spacing-8" pb="spacing-16">
            <HStack gap="spacing-8" align="center">
                {promotion?.c_promotionIcon && isIconURLValid && (
                    <Box
                        bg={!promotion.c_colorWorld ? 'text.primary-inverted' : fontColor}
                        padding="spacing-6"
                        display="flex"
                        alignItems="center"
                        overflow="hidden"
                        borderRadius="full"
                        justifyContent="center"
                        width="1.75rem"
                        height="1.75rem"
                    >
                        <Image
                            alt=""
                            src={promotion.c_promotionIcon}
                            objectFit="contain"
                            flexShrink={0}
                            onError={() => setIsIconURLValid(false)}
                        />
                    </Box>
                )}
                <Text color={fontColor} textStyle="headings.small">
                    {promotion.name}
                </Text>
            </HStack>

            <Box lineClamp={isOpen ? undefined : 1} textStyle="body.small" color="text.primary">
                <StyledContent content={promotion?.calloutMsg} />
            </Box>

            {isOpen && promotion.details && (
                <Box
                    ref={(el) => el?.focus()}
                    tabIndex="-1"
                    textStyle="body.small"
                    color="text.primary"
                    className="leap-container"
                >
                    <StyledContent content={promotion?.details} />
                </Box>
            )}

            {promotion.details && (
                <Link
                    as="button"
                    variant="secondary"
                    underline
                    size="small"
                    onClick={() => setIsOpen(!isOpen)}
                    aria-expanded={isOpen}
                    color="text.primary"
                >
                    {isOpen ? (
                        <FormattedMessage
                            id="leap.promotion.show_less"
                            defaultMessage="Show less"
                        />
                    ) : (
                        <FormattedMessage
                            id="leap.promotion.show_more"
                            defaultMessage="Show more"
                        />
                    )}
                    {isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                </Link>
            )}
        </VStack>
    )
}

PromotionItem.propTypes = {
    promotion: PropTypes.object,
    isActive: PropTypes.bool
}

const PromotionBanner = ({product}) => {
    const {formatMessage} = useIntl()
    const [activeIndex, setActiveIndex] = useState(0)

    const {promotions, loading} = useProductSpecialOffers(product, null, 'xpdp')

    if (loading || !promotions?.length) return null

    return (
        <Box
            as="section"
            aria-label={formatMessage({
                id: 'leap.promotion.assistive_msg.section_label',
                defaultMessage: 'promotion'
            })}
            width="full"
            position="relative"
            backgroundColor="background.section.strong"
            color="text.primary"
            borderRadius="xs"
            paddingBlock="spacing-16"
            paddingInline="spacing-16"
            sx={{
                '--container-inline-padding': {base: 'spacing.spacing-24', s: 'spacing.spacing-32'},
                '--container-total-inline-padding': {
                    base: 'spacing.spacing-24',
                    s: 'spacing.spacing-32'
                }
            }}
        >
            <Slider
                variant="promotionBanner"
                sliderLabel="Product Promotions"
                onIndexChange={(index) => setActiveIndex(index)}
            >
                {promotions.map((promo, idx) => (
                    <PromotionItem
                        key={promo.id}
                        promotion={promo}
                        isActive={activeIndex === idx}
                    />
                ))}
            </Slider>
        </Box>
    )
}

PromotionBanner.propTypes = {
    product: PropTypes.object
}

export default PromotionBanner
